/**
 * Export PDF simplifié avec fallback manuel
 */
import { jsPDF } from 'jspdf';

// Essayer d'importer autoTable, mais ne pas échouer si ça ne marche pas
let autoTable = null;
try {
  autoTable = require('jspdf-autotable').default;
} catch (e) {
  console.warn('jspdf-autotable non disponible, utilisation du fallback manuel');
}

/**
 * Dessine un tableau simple manuellement (fallback)
 */
const drawSimpleTable = (doc, headers, rows, startY = 40) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 10;
  const tableWidth = pageWidth - (margin * 2);
  const columnWidth = tableWidth / headers.length;
  const rowHeight = 7; // Augmenté pour la police plus grande
  
  let currentY = startY;
  
  // Dessiner les en-têtes
  doc.setFillColor(32, 38, 84); // Bleu foncé
  doc.setTextColor(255, 255, 255); // Texte blanc
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  
  // Rectangle pour les en-têtes
  doc.rect(margin, currentY, tableWidth, rowHeight, 'F');
  
  // Texte des en-têtes
  headers.forEach((header, index) => {
    const x = margin + (index * columnWidth) + 2;
    const y = currentY + 5; // Ajusté pour la police plus grande
    doc.text(String(header).substring(0, 12), x, y);
  });
  
  currentY += rowHeight;
  
  // Dessiner les lignes de données
  doc.setTextColor(0, 0, 0); // Texte noir
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  
  rows.forEach((row, rowIndex) => {
    // Couleur alternée pour les lignes
    if (rowIndex % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, currentY, tableWidth, rowHeight, 'F');
    }
    
    // Texte de la ligne
    row.forEach((cell, colIndex) => {
      const x = margin + (colIndex * columnWidth) + 2;
      const y = currentY + 5; // Ajusté pour la police plus grande
      const cellText = String(cell || '').substring(0, 15);
      doc.text(cellText, x, y);
    });
    
    currentY += rowHeight;
    
    // Nouvelle page si nécessaire
    if (currentY > doc.internal.pageSize.height - 20) {
      doc.addPage();
      currentY = 20;
    }
  });
  
  return currentY;
};

/**
 * Export vers PDF avec mise en page simple
 */
export const exportToPDF = (data, filename = 'export', options = {}) => {
  try {
    // console.log('🔄 Export PDF simple...', { count: data?.length });
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Aucune donnée à exporter');
    }

    // Créer le document PDF
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // console.log('📄 Document PDF créé');

    // Titre du document
    const title = options.title || 'Export de données';
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(32, 38, 84); // Couleur bleue pour le titre
    doc.text(title, 148, 20, { align: 'center' });

    // Ligne de séparation sous le titre
    doc.setLineWidth(0.5);
    doc.setDrawColor(32, 38, 84);
    doc.line(50, 25, 246, 25);

    // Date de génération
    const currentDate = new Date().toLocaleDateString('fr-FR');
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100); // Gris pour la date
    doc.text(`Généré le: ${currentDate}`, 148, 32, { align: 'center' });

    // Extraire les en-têtes et préparer les données
    const headers = Object.keys(data[0]);
    const rows = data.map(item => 
      headers.map(header => {
        let value = item[header];
        
        // Nettoyer les montants FCFA (remplacer les / par des espaces)
        if (typeof value === 'string' && value.includes('FCFA')) {
          value = value.replace(/\//g, ' ');
        }
        
        return value !== null && value !== undefined ? String(value) : '';
      })
    );

    // console.log('📋 Création du tableau...', { headers: headers.length, rows: rows.length });

    // Essayer d'utiliser autoTable si disponible, sinon fallback manuel
    if (autoTable && typeof autoTable === 'function') {
      try {
        // console.log('🔧 Utilisation d\'autoTable...');
        
        autoTable(doc, {
          head: [headers],
          body: rows,
          startY: 45,
          theme: 'grid',
          styles: {
            fontSize: 9,
            cellPadding: 2,
            overflow: 'linebreak',
            cellWidth: 'wrap'
          },
          headStyles: {
            fillColor: [32, 38, 84], // Bleu foncé
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 10
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245] // Gris clair
          },
          margin: { top: 45, left: 8, right: 8 }
        });
        
        // console.log('✅ Tableau autoTable créé avec succès');
        
      } catch (autoTableError) {
        console.warn('⚠️ autoTable échoué, utilisation du fallback manuel:', autoTableError.message);
        drawSimpleTable(doc, headers, rows, 45);
        // console.log('✅ Tableau manuel créé avec succès');
      }
    } else {
      // console.log('🔧 autoTable non disponible, utilisation du fallback manuel...');
      drawSimpleTable(doc, headers, rows, 45);
      // console.log('✅ Tableau manuel créé avec succès');
    }

    // console.log('💾 Sauvegarde du fichier PDF...');

    // Sauvegarder le fichier
    doc.save(`${filename}.pdf`);
    
    // console.log('✅ Export PDF terminé avec succès');
    
    return { 
      success: true, 
      message: `Export PDF réussi: ${data.length} lignes exportées` 
    };
    
  } catch (error) {
    console.error('❌ Erreur export PDF:', error);
    return { 
      success: false, 
      message: error.message || 'Erreur lors de l\'export PDF'
    };
  }
}; 